import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Role } from "@/types/user";
import type { TenantUserSortField, UserRoleFilter } from "@/types/user";
import type {
  GuesteriaAdmin,
  GuesteriaAdminSortField,
} from "@/types/guesteria-admin";
import type { UserProfile } from "@/types/user-profile";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";
import { useStaffAvailabilityRange } from "@/hooks/useStaffSchedule";
import {
  useDeleteUser,
  useUpdateUser,
  useUserFilterOptions,
  useUsers,
  useUsersCount,
  useUsersKpis,
} from "../hooks/useUsers";
import {
  useDeleteGuesteriaAdmin,
  useGuesteriaAdminFilterOptions,
  useGuesteriaAdmins,
  useToggleGuesteriaAdminActive,
} from "../hooks/useGuesteriaAdmins";
import { useSorting } from "@/hooks/useSorting";
import { useAdminImpersonation } from "@/hooks/useAdminImpersonation";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { UsersTable } from "./UsersTable";
import { UsersKpiPanel } from "./UsersKpiPanel";
import {
  DEFAULT_USERS_FILTERS,
  type UsersFilterOption,
  UsersFiltersPopover,
  UsersFiltersState,
} from "./UsersFiltersPopover";

const perPage = 10;

const mapGuesteriaAdminToUserProfile = (
  admin: GuesteriaAdmin,
): UserProfile => ({
  id: admin.id,
  keycloakUserId: admin.id,
  tenantId: "",
  role: "GUESTERIA_ADMIN",
  status: admin.enabled ? "ACTIVE" : "INACTIVE",
  firstName: admin.firstName ?? admin.username,
  lastName: admin.lastName ?? "",
  email: admin.email,
  createdAt: new Date(admin.createdAt),
  updatedAt: new Date(admin.updatedAt),
  customRoles: admin.customRoles,
  accessGroups: admin.accessGroups,
});

type StaffStatusFilter = "all" | "on-site" | "not-on-site";

const STAFF_STATUS_LABEL_KEYS: Record<StaffStatusFilter, string> = {
  all: "staff.filters.all",
  "on-site": "staff.filters.onSite",
  "not-on-site": "staff.filters.notOnSite",
};

const isStaffStatusFilter = (value: string): value is StaffStatusFilter =>
  value === "all" || value === "on-site" || value === "not-on-site";

const SYSTEM_ROLE_FILTER_PREFIX = "system:";
const CUSTOM_ROLE_FILTER_PREFIX = "custom:";

const resolveTenantUserSortField = (
  value: string | null,
): TenantUserSortField | undefined => {
  switch (value) {
    case "status":
    case "name":
    case "email":
    case "phone":
    case "role":
    case "group":
      return value;
    default:
      return undefined;
  }
};

type UsersTabProps = {
  backTo: string | null;
  tenantId?: string;
  canAddUser: boolean;
  canOpenUserDetails: boolean;
  canViewUserDetails: boolean;
  canImpersonateUsers: boolean;
  canEditGuesteriaAdmin: boolean;
  canDeleteUser: boolean;
  canAdministrateUsers: boolean;
  showTenantColumn: boolean;
  possibleRoles: UserRoleFilter[];
  userListSource: "users" | "guesteria-admins";
};

export const UsersTab: React.FC<UsersTabProps> = ({
  backTo,
  tenantId,
  canAddUser,
  canOpenUserDetails,
  canViewUserDetails,
  canImpersonateUsers,
  canEditGuesteriaAdmin,
  canDeleteUser,
  canAdministrateUsers,
  showTenantColumn,
  possibleRoles,
  userListSource,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userProfile } = useAuthenticatedUser();
  const { impersonateUser, isStartingImpersonation } = useAdminImpersonation();
  const { sortField, sortDirection, handleSort } = useSorting();
  const [currentPage, setCurrentPage] = useState(1);
  const [impersonatingUserId, setImpersonatingUserId] = useState<string | null>(
    null,
  );
  const [usersFilters, setUsersFilters] = useState<UsersFiltersState>(
    DEFAULT_USERS_FILTERS,
  );
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [statusFilter, setStatusFilter] = useState<StaffStatusFilter>("all");

  const hotelId = useCurrentHotelId();
  const today = format(new Date(), "yyyy-MM-dd");
  const showOnSiteFilter = possibleRoles.includes(Role.STAFF);

  const useGuesteriaAdminList = userListSource === "guesteria-admins";
  const deleteGuesteriaAdminMutation = useDeleteGuesteriaAdmin();
  const toggleGuesteriaAdminActiveMutation = useToggleGuesteriaAdminActive();
  const deleteUserMutation = useDeleteUser();
  const updateUserMutation = useUpdateUser();
  const filterOptionsQuery = useMemo(
    () => ({ roles: possibleRoles, tenantId }),
    [possibleRoles, tenantId],
  );
  const { data: tenantUserFilterOptions } = useUserFilterOptions(
    filterOptionsQuery,
    !useGuesteriaAdminList,
  );
  const { data: guesteriaAdminFilterOptions } = useGuesteriaAdminFilterOptions(
    useGuesteriaAdminList,
  );
  const userFilterOptions = useGuesteriaAdminList
    ? guesteriaAdminFilterOptions
    : tenantUserFilterOptions;

  const positionOptions = useMemo<UsersFilterOption[]>(
    () =>
      (userFilterOptions?.positions ?? []).map((position) => ({
        value: position,
        label: position,
      })),
    [userFilterOptions?.positions],
  );
  const roleOptions = useMemo<UsersFilterOption[]>(
    () =>
      (userFilterOptions?.roles ?? []).map((role) => {
        let label = role.name;
        if (role.type === "system") {
          if (role.id === Role.TENANT_ADMIN) {
            label = t("users.table.tenantAdmin");
          } else if (role.id === Role.HOTEL_ADMIN) {
            label = t("users.table.hotelAdmin");
          } else if (role.id === Role.FRONT_DESK) {
            label = t("users.table.frontDesk");
          } else if (role.id === Role.STAFF) {
            label = t("users.table.staff");
          } else if (role.id === "GUESTERIA_ADMIN") {
            label = t("users.table.guesteriaAdmin", "Guesteria Admin");
          }
        }

        return {
          value: `${role.type}:${role.id}`,
          label,
        };
      }),
    [t, userFilterOptions?.roles],
  );
  const groupOptions = useMemo<UsersFilterOption[]>(
    () =>
      (userFilterOptions?.groups ?? []).map((group) => ({
        value: group.id,
        label: group.name,
      })),
    [userFilterOptions?.groups],
  );

  const isLocallyFiltered =
    showOnSiteFilter && !useGuesteriaAdminList && statusFilter !== "all";

  const { data: todayAvailability } = useStaffAvailabilityRange(
    hotelId ?? "",
    today,
    today,
  );

  const staffStatusMap = useMemo(() => {
    const map = new Map<string, Exclude<StaffStatusFilter, "all">>();
    if (!todayAvailability) return map;
    for (const availability of todayAvailability) {
      const day = availability.days[0];
      if (!day) continue;
      const nextStatus: Exclude<StaffStatusFilter, "all"> = day.isAvailable
        ? "on-site"
        : "not-on-site";
      map.set(availability.staffId, nextStatus);
      if (availability.profileId) {
        map.set(availability.profileId, nextStatus);
      }
    }
    return map;
  }, [todayAvailability]);

  const filters = useMemo(() => {
    const hasExplicitRoleFilter = usersFilters.roles.length > 0;
    const selectedRoles = usersFilters.roles
      .filter((value) => value.startsWith(SYSTEM_ROLE_FILTER_PREFIX))
      .map((value) => value.slice(SYSTEM_ROLE_FILTER_PREFIX.length))
      .filter((role): role is UserRoleFilter =>
        possibleRoles.includes(role as UserRoleFilter),
      );
    const selectedCustomRoleIds = usersFilters.roles
      .filter((value) => value.startsWith(CUSTOM_ROLE_FILTER_PREFIX))
      .map((value) => value.slice(CUSTOM_ROLE_FILTER_PREFIX.length));
    let roles: UserRoleFilter[] | undefined = possibleRoles;
    if (hasExplicitRoleFilter) {
      roles = selectedRoles.length > 0 ? selectedRoles : undefined;
    }

    return {
      roles,
      customRoleIds:
        selectedCustomRoleIds.length > 0 ? selectedCustomRoleIds : undefined,
      positions:
        usersFilters.positions.length > 0 ? usersFilters.positions : undefined,
      accessGroupIds:
        usersFilters.groups.length > 0 ? usersFilters.groups : undefined,
      sortField: resolveTenantUserSortField(sortField),
      sortDirection,
      perPage: isLocallyFiltered ? 1000 : perPage,
      currentPage: isLocallyFiltered ? 1 : currentPage,
      tenantId,
      search: usersFilters.userQuery || undefined,
      // Only STAFF users have a real active/inactive concept in the data
      // model today, so this only narrows STAFF rows (see backend
      // matchesStaffActivityFilter). Applying it when both are selected
      // would be a no-op, so only send it for a single selection.
      statusActivity:
        usersFilters.statuses.length === 1
          ? (usersFilters.statuses[0] as "active" | "inactive")
          : undefined,
    };
  }, [
    currentPage,
    isLocallyFiltered,
    possibleRoles,
    sortDirection,
    sortField,
    tenantId,
    usersFilters.roles,
    usersFilters.positions,
    usersFilters.groups,
    usersFilters.statuses,
    usersFilters.userQuery,
  ]);

  const guesteriaAdminFilters = useMemo(() => {
    const sortBy: GuesteriaAdminSortField | undefined =
      sortField === "email" || sortField === "createdAt"
        ? sortField
        : undefined;

    const selectedCustomRoleIds = usersFilters.roles
      .filter((value) => value.startsWith(CUSTOM_ROLE_FILTER_PREFIX))
      .map((value) => value.slice(CUSTOM_ROLE_FILTER_PREFIX.length));
    const includeBaseRole = usersFilters.roles.includes(
      `${SYSTEM_ROLE_FILTER_PREFIX}GUESTERIA_ADMIN`,
    );
    let enabled: "true" | "false" | undefined;
    if (usersFilters.statuses.length === 1) {
      enabled = usersFilters.statuses[0] === "active" ? "true" : "false";
    }

    return {
      limit: perPage,
      offset: (currentPage - 1) * perPage,
      search: usersFilters.userQuery || undefined,
      enabled,
      customRoleIds:
        selectedCustomRoleIds.length > 0 ? selectedCustomRoleIds : undefined,
      accessGroupIds:
        usersFilters.groups.length > 0 ? usersFilters.groups : undefined,
      includeBaseRole: includeBaseRole ? ("true" as const) : undefined,
      sortBy,
      sortOrder: sortDirection ?? undefined,
    };
  }, [
    currentPage,
    sortDirection,
    sortField,
    usersFilters.groups,
    usersFilters.roles,
    usersFilters.statuses,
    usersFilters.userQuery,
  ]);

  const { data: usersResponse, isLoading: isUsersLoading } = useUsers(
    filters,
    !useGuesteriaAdminList,
  );
  const { data: totalUsersResponse = 0 } = useUsersCount(
    filters,
    !useGuesteriaAdminList,
  );
  const usersKpiFilters = useMemo(
    () => ({
      hotelId: hotelId ?? undefined,
      date: today,
    }),
    [hotelId, today],
  );
  const showUsersKpiPanel =
    showOnSiteFilter && !useGuesteriaAdminList && Boolean(hotelId);
  const { data: usersKpis, isLoading: isUsersKpisLoading } = useUsersKpis(
    usersKpiFilters,
    showUsersKpiPanel,
  );
  const { data: guesteriaAdminsResponse, isLoading: isGuesteriaAdminsLoading } =
    useGuesteriaAdmins(guesteriaAdminFilters, useGuesteriaAdminList);

  const guesteriaAdminUsers = useMemo(
    () =>
      (guesteriaAdminsResponse?.items ?? []).map(
        mapGuesteriaAdminToUserProfile,
      ),
    [guesteriaAdminsResponse?.items],
  );
  const allUsers = useMemo(
    () => (useGuesteriaAdminList ? guesteriaAdminUsers : (usersResponse ?? [])),
    [useGuesteriaAdminList, guesteriaAdminUsers, usersResponse],
  );
  const totalUsers = useGuesteriaAdminList
    ? (guesteriaAdminsResponse?.total ?? 0)
    : totalUsersResponse;
  const isLoading = useGuesteriaAdminList
    ? isGuesteriaAdminsLoading
    : isUsersLoading;

  const filteredUsers = useMemo(() => {
    if (!isLocallyFiltered) return allUsers;
    return allUsers.filter((user) => {
      const staffId = user.staff?.id;
      const currentStatus =
        (staffId ? staffStatusMap.get(staffId) : undefined) ??
        staffStatusMap.get(user.id);
      if (statusFilter === "on-site") return currentStatus === "on-site";
      return currentStatus !== "on-site";
    });
  }, [allUsers, isLocallyFiltered, staffStatusMap, statusFilter]);

  const users = useMemo(() => {
    if (!isLocallyFiltered) return filteredUsers;
    const start = (currentPage - 1) * perPage;
    return filteredUsers.slice(start, start + perPage);
  }, [filteredUsers, isLocallyFiltered, currentPage]);

  const totalPages = isLocallyFiltered
    ? Math.max(1, Math.ceil(filteredUsers.length / perPage))
    : Math.max(1, Math.ceil(totalUsers / perPage));

  const handleImpersonateUser = useCallback(
    async (userId: string) => {
      setImpersonatingUserId(userId);

      try {
        await impersonateUser(userId);
      } finally {
        setImpersonatingUserId((currentUserId) =>
          currentUserId === userId ? null : currentUserId,
        );
      }
    },
    [impersonateUser],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete) {
      return;
    }

    try {
      if (useGuesteriaAdminList) {
        await deleteGuesteriaAdminMutation.mutateAsync(userToDelete.id);
        toast.success(t("users.delete.success"));
      } else {
        await deleteUserMutation.mutateAsync(userToDelete.id);
        toast.success(t("users.delete.userSuccess"));
      }
      if (users.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      setUserToDelete(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : undefined;
      toast.error(
        message ??
          (useGuesteriaAdminList
            ? t("users.delete.failed")
            : t("users.delete.userFailed")),
      );
    }
  }, [
    currentPage,
    deleteGuesteriaAdminMutation,
    deleteUserMutation,
    t,
    userToDelete,
    useGuesteriaAdminList,
    users.length,
  ]);

  const handleToggleUserActive = useCallback(
    async (userId: string, isActive: boolean) => {
      try {
        if (useGuesteriaAdminList) {
          await toggleGuesteriaAdminActiveMutation.mutateAsync({
            id: userId,
            enabled: isActive,
          });
        } else {
          await updateUserMutation.mutateAsync({
            id: userId,
            data: { isActive },
          });
        }
        toast.success(
          isActive
            ? t("users.table.userActivated")
            : t("users.table.userDeactivated"),
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : undefined;
        toast.error(message ?? t("users.form.failedToUpdateUser"));
      }
    },
    [
      t,
      toggleGuesteriaAdminActiveMutation,
      updateUserMutation,
      useGuesteriaAdminList,
    ],
  );

  const deleteTargetName = userToDelete
    ? `${userToDelete.firstName} ${userToDelete.lastName}`.trim() ||
      userToDelete.email
    : "";

  const handleStatusFilterChange = (value: string) => {
    if (!isStaffStatusFilter(value)) {
      return;
    }

    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleUserSort = (field: string): void => {
    setCurrentPage(1);
    handleSort(field);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {backTo ? (
            <Button variant="new_outline" onClick={() => navigate(backTo)}>
              {t("users.page.back")}
            </Button>
          ) : null}
          <UsersFiltersPopover
            applied={usersFilters}
            positionOptions={positionOptions}
            roleOptions={roleOptions}
            groupOptions={groupOptions}
            onApply={(next) => {
              setUsersFilters(next);
              setCurrentPage(1);
            }}
          />
          {showOnSiteFilter ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="new_outline"
                  className="w-[120px] justify-between"
                >
                  {t(STAFF_STATUS_LABEL_KEYS[statusFilter])}
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[120px]">
                <DropdownMenuRadioGroup
                  value={statusFilter}
                  onValueChange={handleStatusFilterChange}
                >
                  <DropdownMenuRadioItem value="all">
                    {t(STAFF_STATUS_LABEL_KEYS.all)}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="on-site">
                    {t(STAFF_STATUS_LABEL_KEYS["on-site"])}
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="not-on-site">
                    {t(STAFF_STATUS_LABEL_KEYS["not-on-site"])}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>

        <Button
          variant="new_default"
          size="default"
          onClick={() => {
            const params = new URLSearchParams();

            if (tenantId) {
              params.set("tenantId", tenantId);
            }

            if (backTo) {
              params.set("backTo", backTo);
            }

            const query = params.toString();
            navigate(query ? `/users/new?${query}` : "/users/new");
          }}
          disabled={!canAddUser}
          title={
            !canAddUser
              ? t("common.insufficientPermissions", "Insufficient permissions")
              : undefined
          }
        >
          <Plus />
          {t("users.page.addUser")}
        </Button>
      </div>

      {showUsersKpiPanel ? (
        <UsersKpiPanel summary={usersKpis} isLoading={isUsersKpisLoading} />
      ) : null}

      <UsersTable
        currentPage={currentPage}
        totalPages={totalPages}
        users={users || []}
        handleSort={handleUserSort}
        isLoading={isLoading}
        setCurrentPage={setCurrentPage}
        sortField={sortField}
        sortDirection={sortDirection}
        canOpenUserDetails={canOpenUserDetails}
        canViewUserDetails={canViewUserDetails}
        canImpersonateUsers={canImpersonateUsers}
        currentUserId={userProfile?.id}
        isStartingImpersonation={isStartingImpersonation}
        impersonatingUserId={impersonatingUserId}
        onImpersonateUser={handleImpersonateUser}
        canEditGuesteriaAdmin={canEditGuesteriaAdmin}
        canDeleteUser={canDeleteUser}
        showTenantColumn={showTenantColumn}
        onDeleteUser={setUserToDelete}
        isDeletingUser={
          deleteGuesteriaAdminMutation.isPending || deleteUserMutation.isPending
        }
        onToggleUserActive={
          canAdministrateUsers ? handleToggleUserActive : undefined
        }
        staffStatusMap={showOnSiteFilter ? staffStatusMap : undefined}
      />

      <Dialog
        open={userToDelete !== null}
        onOpenChange={(open) => !open && setUserToDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {useGuesteriaAdminList
                ? t("users.delete.title")
                : t("users.delete.userTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("users.delete.description", { name: deleteTargetName })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUserToDelete(null)}
              disabled={
                deleteGuesteriaAdminMutation.isPending ||
                deleteUserMutation.isPending
              }
            >
              {t("users.delete.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmDelete()}
              disabled={
                deleteGuesteriaAdminMutation.isPending ||
                deleteUserMutation.isPending
              }
            >
              {deleteGuesteriaAdminMutation.isPending ||
              deleteUserMutation.isPending
                ? t("users.delete.deleting")
                : t("users.delete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
